import React, {useContext, useMemo, useState} from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    BarElement,
    Chart,
    Filler,
} from 'chart.js';
import {Doughnut, Line} from 'react-chartjs-2';
import {getIdWithToken} from "../../../utils/storage/Token";
import {EventUpdatedContext} from "../../../store/EventUpdatedContext";
import {useFetchStats} from "../../../service/Business/StatsService";
import TypeDate from "../../../types/TypeDate";
import "./ContentStatistic.scss";
import FCSortableHeader from "../../FCObjets/FCSortableHeader";
import {useTableSort} from "../../../hook/useTableSort";
import formatEuro from "../../../utils/formatEuro";

type StatsSortKey = "month" | "caNet" | "urssaf" | "tva" | "totalNet";
interface StatsRow {
    monthIndex: number;
    monthLabel: string;
    caNet: number;
    urssaf: number;
    tva: number;
    totalNet: number;
}

type contentStatProps = {
    type: "urssaf" | "annual";
};

function ContentStatistic(props: contentStatProps) {

    const [year, setYear] = useState<number>(new Date().getFullYear());
    const {cptUpdated} = useContext(EventUpdatedContext);
    const {data: stat} = useFetchStats(getIdWithToken(), year, props.type, cptUpdated);

    ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, BarElement, Filler);

    const labels = TypeDate.monthsLong;

    Chart.defaults.color = "var(--text-muted)";

    const goldColor = 'rgba(255, 219, 175, 0.9)';
    const goldColorLight = 'rgba(255, 219, 175, 0.1)';
    const greenColor = 'rgba(102, 217, 160, 0.8)';
    const pinkColor = 'rgba(253, 121, 168, 0.8)';
    const blueColor = 'rgba(100, 181, 246, 0.8)';

    // Line Chart config
    const optionsLineChart = {
        responsive: true,
        maintainAspectRatio: true,
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {color: '#999'}
            },
            title: {display: false},
            tooltip: {
                callbacks: {
                    label: (item: any) => `${item.dataset.label}: ${item.formattedValue} €`,
                },
            },
        },
        scales: {
            x: {
                grid: {color: 'rgba(255,255,255,0.05)'},
                ticks: {color: '#666', font: {size: 10}}
            },
            y: {
                type: 'linear' as const,
                display: true,
                position: 'left' as const,
                min: 0,
                grid: {color: 'rgba(255,255,255,0.05)'},
                ticks: {color: '#666'}
            },
            y1: {
                type: 'linear' as const,
                display: true,
                position: 'right' as const,
                grid: {drawOnChartArea: false},
                min: 0,
                max: 500,
                ticks: {color: '#666'}
            },
        },
    };

    const dataLineChart = {
        labels,
        datasets: [
            {
                label: 'Total net',
                data: stat ? stat.dataTotalNet : [],
                borderColor: goldColor,
                backgroundColor: goldColorLight,
                fill: true,
                yAxisID: 'y',
                tension: 0.4,
            },
            {
                label: 'Chiffre affaire net',
                data: stat ? stat.dataCANet : [],
                borderColor: greenColor,
                backgroundColor: 'transparent',
                yAxisID: 'y',
                tension: 0.4,
            },
            {
                label: 'TVA déductible',
                data: stat ? stat.dataTvaDeductible : [],
                borderColor: blueColor,
                backgroundColor: 'transparent',
                yAxisID: 'y1',
                tension: 0.4,
            },
        ],
    };

    // Doughnut chart config
    const dataDoughnut = {
        labels: ['Net salarié', 'Cotisations', 'TVA déductible'],
        datasets: [
            {
                data: stat ? [stat.cumulAnnuelCANet, stat.cumulAnnuelUrssaf, stat.cumulAnnuelTvaDeductible] : [],
                backgroundColor: [goldColor, pinkColor, blueColor],
                borderColor: ['transparent', 'transparent', 'transparent'],
                borderWidth: 0,
                cutout: '70%',
            },
        ],
    };

    const optionsDoughnut = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {color: '#999', padding: 16}
            },
            tooltip: {
                callbacks: {
                    label: (ttItem: any) => formatEuro(ttItem.parsed)
                }
            }
        },
    };

    const monthlyRows: StatsRow[] = useMemo(() => {
        if (!stat) return [];
        return labels.map((m, i) => ({
            monthIndex: i,
            monthLabel: m,
            caNet: stat.dataCANet?.[i] || 0,
            urssaf: stat.dataUrssaf?.[i] || 0,
            tva: stat.dataTvaDeductible?.[i] || 0,
            totalNet: stat.dataTotalNet?.[i] || 0,
        }));
    }, [stat, labels]);

    const {sortedData: sortedMonthlyRows, sortState, handleSort} = useTableSort<StatsRow, StatsSortKey>(
        monthlyRows,
        {
            month: (r) => r.monthIndex,
            caNet: (r) => r.caNet,
            urssaf: (r) => r.urssaf,
            tva: (r) => r.tva,
            totalNet: (r) => r.totalNet,
        },
    );

    const brutAnnuel = stat ? (stat.cumulAnnuelCANet + stat.cumulAnnuelUrssaf) : 0;
    const netAnnuel = stat ? stat.cumulAnnuelCANet : 0;
    const netPlusTVA = stat ? (stat.cumulAnnuelCANet + stat.cumulAnnuelTvaDeductible) : 0;

    return (
        <div className="statsPage">
            {/* Header */}
            <div className="statsHeader">
                <div>
                    <h1 className="statsTitle">
                        Statistiques {props.type === "annual" ? "Annuelles" : "URSSAF"} {year}
                    </h1>
                    <p className="statsSubtitle">
                        {props.type === "annual"
                            ? "Performance annuelle et répartition de vos revenus"
                            : "Détails des cotisations et charges URSSAF"}
                    </p>
                </div>
                <div className="statsHeaderRight">
                    <div className="statsYearNav">
                        <button className="statsYearBtn" onClick={() => setYear(year - 1)}>
                            <span className="material-symbols-outlined">chevron_left</span>
                        </button>
                        <span className="statsYear">{year}</span>
                        <button className="statsYearBtn" onClick={() => setYear(year + 1)}>
                            <span className="material-symbols-outlined">chevron_right</span>
                        </button>
                    </div>
                </div>
            </div>

            {stat && (
                <>
                    {/* KPI Cards */}
                    <div className="statsKpiGrid">
                        <div className="statsKpiCard statsKpiBrut">
                            <div className="statsKpiLabel">Brut annuel</div>
                            <div className="statsKpiValueLg">{formatEuro(brutAnnuel, 0)}</div>
                            <div className="statsKpiSub">CA brut {year}</div>
                        </div>
                        <div className="statsKpiCard">
                            <div className="statsKpiLabel">Net annuel</div>
                            <div className="statsKpiValue">{formatEuro(netAnnuel, 0)}</div>
                            <div className="statsKpiSub">Après cotisations</div>
                        </div>
                        <div className="statsKpiCard">
                            <div className="statsKpiLabel">Net + TVA</div>
                            <div className="statsKpiValue">{formatEuro(netPlusTVA, 0)}</div>
                            <div className="statsKpiSub">Résultat final toutes charges</div>
                        </div>
                    </div>

                    {/* Charts */}
                    <div className="statsChartsGrid">
                        <div className="statsChartCard">
                            <h3 className="statsChartTitle">Évolution du CA</h3>
                            <p className="statsChartSubtitle">Performance annuelle consolidée</p>
                            <div className="statsChartContainer">
                                <Line options={optionsLineChart} data={dataLineChart}/>
                            </div>
                        </div>
                        <div className="statsChartCard">
                            <h3 className="statsChartTitle">Répartition</h3>
                            <p className="statsChartSubtitle">Structure de revenus</p>
                            <div className="statsPieContainer">
                                <Doughnut options={optionsDoughnut} data={dataDoughnut}/>
                            </div>
                        </div>
                    </div>

                    {/* Monthly Details Table */}
                    <div className="statsSectionHeader">
                        <div>
                            <h2 className="statsSectionTitle">Détails Mensuels</h2>
                            <p className="statsSectionSubtitle">Volume d'activité par période</p>
                        </div>
                    </div>

                    {/* Desktop table */}
                    <div className="statsTableWrapper">
                        <table className="statsTable">
                            <thead>
                            <tr>
                                <th><FCSortableHeader label="Mois" sortKey="month" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="CA Net" sortKey="caNet" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="URSSAF" sortKey="urssaf" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="TVA déductible" sortKey="tva" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="Total Net" sortKey="totalNet" sortState={sortState} onSort={handleSort}/></th>
                            </tr>
                            </thead>
                            <tbody>
                            {sortedMonthlyRows.map((row) => (
                                <tr key={row.monthIndex}>
                                    <td><span className="statsCellMonth">{row.monthLabel}</span></td>
                                    <td><span className="statsCellAmount">{formatEuro(row.caNet)}</span></td>
                                    <td>{formatEuro(row.urssaf)}</td>
                                    <td>{formatEuro(row.tva)}</td>
                                    <td><span className="statsCellAmountGold">{formatEuro(row.totalNet)}</span></td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="statsMobileList">
                        {labels.map((month, i) => {
                            const caNet = stat.dataCANet?.[i] || 0;
                            const urssaf = stat.dataUrssaf?.[i] || 0;
                            const tva = stat.dataTvaDeductible?.[i] || 0;
                            const totalNet = stat.dataTotalNet?.[i] || 0;
                            if (caNet === 0 && urssaf === 0 && tva === 0) return null;
                            return (
                                <div className="statsMobileCard" key={i}>
                                    <div className="statsMobileCardTitle">{month}</div>
                                    <div className="statsMobileCardGrid">
                                        <div className="statsMobileCardItem">
                                            <span className="statsMobileCardItemLabel">CA Net</span>
                                            <span className="statsMobileCardItemValue">{formatEuro(caNet)}</span>
                                        </div>
                                        <div className="statsMobileCardItem">
                                            <span className="statsMobileCardItemLabel">Total Net</span>
                                            <span className="statsMobileCardItemValueGold">{formatEuro(totalNet)}</span>
                                        </div>
                                        <div className="statsMobileCardItem">
                                            <span className="statsMobileCardItemLabel">URSSAF</span>
                                            <span className="statsMobileCardItemValue">{formatEuro(urssaf)}</span>
                                        </div>
                                        <div className="statsMobileCardItem">
                                            <span className="statsMobileCardItemLabel">TVA déd.</span>
                                            <span className="statsMobileCardItemValue">{formatEuro(tva)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

export default ContentStatistic;
